import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useLanguageSwitch from '../../hooks/useLanguageSwitch';

interface LanguageSwitcherProps {
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ className = '' }) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const language = i18n.language.split('-')[0] as 'en' | 'fr' | 'ar';
  const switchLanguage = useLanguageSwitch();

  const languages = [
    { code: 'en', name: t('language.english') },
    { code: 'fr', name: t('language.french') },
    { code: 'ar', name: t('language.arabic') }
  ];

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleLanguageChange = (langCode: 'en' | 'fr' | 'ar') => {
    if (langCode === language) {
      setIsOpen(false);
      return;
    }
    
    // Use our new switchLanguage hook
    switchLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="uppercase mr-1">{language}</span>
        <svg
          className={`h-5 w-5 text-gray-400 ${isOpen ? 'rotate-180' : ''} transition-transform`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="language-menu"
        >
          <div className="py-1" role="none">
            {languages.map((lang) => (
                  <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code as 'en' | 'fr' | 'ar')}
                className={`
                  w-full text-left px-4 py-2 text-sm 
                  ${language === lang.code ? 'bg-gray-100 text-gray-900 font-medium' : 'text-gray-700'} 
                  hover:bg-gray-100 hover:text-gray-900 focus:outline-none
                `}
                role="menuitem"
                  >
                    {lang.name}
                  </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher; 